import { Box, Flex, FormControl, Input, Stack, Tag } from "@chakra-ui/react"
import { ErrorMessage, Formik } from "formik"
import { ComponentProps, FC, useState } from "react"
import { ReactSelect, SelectOption } from "src/components/shared/ReactSelect"
import { ErrorMessageField } from "src/components/ui"
import { DrawerForm } from "src/components/ui/DrawerForm"
import { InputLabel } from "src/components/ui/InputLabel"
import { IContact, IHallBooking, ILocation } from "src/domain/entities"
import { fetchGujaratiSuggestions } from "src/utils/helpers"
import { FormikOnSubmit } from "src/utils/types"
import { IHallBookingAddFormFields } from "."
import moment from "moment"

interface Props extends Omit<ComponentProps<typeof DrawerForm>, "children"> {
	hallBooking: IHallBooking
	handleSubmit: FormikOnSubmit<IHallBookingAddFormFields>
	locationList: ILocation[]
	contactList: IContact[]
	selectedLocation: ILocation | undefined
	setSelectedLocation: (location: ILocation | undefined) => void
	selectedContact: IContact | undefined
	setSelectedContact: (contact: IContact | undefined) => void
}

export const HallBookingUpdateDrawerFormView: FC<Props> = ({
	hallBooking,
	handleSubmit,
	locationList,
	contactList,
	selectedLocation,
	setSelectedLocation,
	selectedContact,
	setSelectedContact,
	...rest
}) => {
	const [nameSuggestions, setNameSuggestions] = useState<string[]>([])

	return (
		<Formik<IHallBookingAddFormFields>
			initialValues={{
				startDate: moment(hallBooking.startDate).format("YYYY-MM-DD"),
				endDate: moment(hallBooking.endDate).format("YYYY-MM-DD"),
				locationId: selectedLocation?.id || "",
				contactId: selectedContact?.id || "",
				reason: hallBooking.reason,
				deposit: hallBooking.deposit,
				paymentRemarks: hallBooking.paymentRemarks,
			}}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ values, isSubmitting, handleChange, setFieldValue }) => {
				const handleNameChange = async (
					e: React.ChangeEvent<HTMLInputElement>,
				) => {
					setFieldValue("reason", e.target.value)
					const suggestions = await fetchGujaratiSuggestions(e.target.value)
					setNameSuggestions(suggestions)
				}
				const locationOptions = locationList.map((location) => ({
					label: location.name,
					value: location.id,
				}))
				const contactOptions = contactList.map((contact) => ({
					label: `${contact.firstName} ${contact.lastName}`,
					value: contact.id,
				}))
				return (
					<DrawerForm
						size="sm"
						headerLabel="Update Hall Booking"
						submitLabel="Save"
						isSubmitting={isSubmitting}
						{...rest}
					>
						<Stack maxWidth={"sm"} marginX={"auto"}>
							{/* Location */}
							<FormControl>
								<InputLabel label="Location" />
								<ReactSelect
									name="locationId"
									value={locationOptions.find(
										(option) => option.value === values.locationId,
									)}
									onChange={(newValue) => {
										setFieldValue(
											"locationId",
											(newValue as SelectOption).value,
										)
									}}
									isDisabled={true}
									options={locationOptions}
									isSearchable
								/>
							</FormControl>
							{/* Contact */}
							<FormControl>
								<InputLabel label="Contact" />
								<ReactSelect
									name="contactId"
									value={contactOptions.find(
										(option) => option.value === values.contactId,
									)}
									onChange={(newValue) => {
										setFieldValue(
											"contactId",
											(newValue as SelectOption).value,
										)
									}}
									isDisabled={true}
									options={contactOptions}
									isSearchable
								/>
							</FormControl>
							{/* Reason */}
							<FormControl>
								<InputLabel label="Reason" />
								<Input
									name="reason"
									placeholder="Reason"
									maxLength={100}
									onChange={handleNameChange}
								/>
								<ErrorMessage
									component={ErrorMessageField}
									name="reason"
								/>
							</FormControl>
							<Box>
								{nameSuggestions.map((el, i) => (
									<Tag
										colorScheme={"green"}
										backgroundColor={"green.50"}
										variant="outline"
										_hover={{
											backgroundColor: "green.100",
										}}
										cursor="pointer"
										margin={0.5}
										onClick={() => {
											setFieldValue("reason", el)
											setNameSuggestions([])
										}}
										key={i}
									>
										{el}
									</Tag>
								))}
							</Box>
							{/* Start & End Date */}
							<Flex className="gap-2">
								<FormControl>
									<InputLabel label="From" />
									<Input
										name="startDate"
										type="date"
										value={values.startDate}
										required
										autoFocus
										onChange={handleChange}
									/>
								</FormControl>
								<FormControl>
									<InputLabel label="To" />
									<Input
										name="endDate"
										type="date"
										value={values.endDate}
										required
										autoFocus
										onChange={handleChange}
									/>
								</FormControl>
							</Flex>
							<FormControl>
								<InputLabel label="Deposit" />
								<Input
									name="deposit"
									type="number"
									value={values.deposit}
									required
									autoFocus
									onChange={handleChange}
								/>
								<ErrorMessage
									component={ErrorMessageField}
									name="deposit"
								/>
							</FormControl>
							<FormControl>
								<InputLabel label="Payment Remarks" />
								<Input
									name="paymentRemarks"
									placeholder="Payment Remarks"
									maxLength={100}
									onChange={handleChange}
								/>
								<ErrorMessage
									component={ErrorMessageField}
									name="paymentRemarks"
								/>
							</FormControl>
						</Stack>
					</DrawerForm>
				)
			}}
		</Formik>
	)
}
